/**
 * PWA icon generation.
 *
 * A tenant supplies ONE brand mark (`branding.iconUrl`) at whatever size it
 * happens to be. Chromium refuses to treat an app as installable without a
 * 192x192 AND a 512x512 icon, and it decodes the image rather than trusting
 * the manifest's `sizes` string — so declaring a 64px favicon as "512x512"
 * does not work. Every icon is therefore RE-DRAWN here at the exact size,
 * onto a canvas, and handed to the manifest as a `data:` URL.
 *
 * The mark is drawn on WHITE, matching the shell's logo-tile rule
 * (frontend-style-guide.md, "Logos vs avatars"): brand marks are drawn for
 * white paper, so a transparent PNG on a dark OS launcher smudges.
 *
 * Cross-origin safety: the image is loaded with `crossOrigin="anonymous"`. A
 * CDN that does not send CORS headers fails the LOAD (rather than tainting
 * the canvas and throwing on `toDataURL`), which lands on the monogram
 * fallback — a letter on the tenant's own primary. So a tenant with an
 * unreachable or non-CORS icon still gets an installable app.
 */

export interface AppIconSet {
  /** 192x192 PNG data URL — Chromium's installability minimum, and iOS's apple-touch-icon. */
  any192: string;
  /** 512x512 PNG data URL — Chromium's splash-screen source. */
  any512: string;
  /** 512x512 PNG data URL drawn inside the maskable safe zone (Android adaptive icons). */
  maskable512: string;
}

const ICON_LOAD_TIMEOUT_MS = 6000;

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const finish = (value: HTMLImageElement | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    // A slow or hanging CDN must never hold up the boot path.
    const timer = setTimeout(() => finish(null), ICON_LOAD_TIMEOUT_MS);
    img.crossOrigin = "anonymous";
    img.onload = () => {
      clearTimeout(timer);
      finish(img.naturalWidth > 0 ? img : null);
    };
    img.onerror = () => {
      clearTimeout(timer);
      finish(null);
    };
    img.src = url;
  });
}

function canvasOf(size: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  return ctx ? { canvas, ctx } : null;
}

/**
 * `inset` is the fraction of the canvas left EMPTY around the mark. 0.12 for a
 * normal icon; 0.22 for a maskable one, whose corners the launcher may crop
 * (the maskable safe zone is the centre 80% circle).
 */
function drawMark(
  ctx: CanvasRenderingContext2D,
  size: number,
  image: HTMLImageElement,
  inset: number,
): void {
  const box = size * (1 - inset * 2);
  // `contain`, never `cover` — cropping a wordmark is the failure the
  // logo-tile rule exists to prevent.
  const scale = Math.min(box / image.naturalWidth, box / image.naturalHeight);
  const w = image.naturalWidth * scale;
  const h = image.naturalHeight * scale;
  ctx.drawImage(image, (size - w) / 2, (size - h) / 2, w, h);
}

function drawMonogram(ctx: CanvasRenderingContext2D, size: number, letter: string, colour: string): void {
  ctx.fillStyle = colour;
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  ctx.font = `700 ${Math.round(size * 0.46)}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Optical centring: a capital letter's visual middle sits slightly below the
  // text baseline midpoint.
  ctx.fillText(letter, size / 2, size / 2 + size * 0.02);
}

function render(
  size: number,
  inset: number,
  image: HTMLImageElement | null,
  letter: string,
  primary: string,
): string | null {
  const made = canvasOf(size);
  if (!made) return null;
  const { canvas, ctx } = made;
  if (image) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
    drawMark(ctx, size, image, inset);
  } else {
    drawMonogram(ctx, size, letter, primary);
  }
  try {
    return canvas.toDataURL("image/png");
  } catch {
    // Tainted canvas — cannot happen with crossOrigin="anonymous" (a
    // non-CORS response fails the load instead), but a thrown SecurityError
    // here must not take the boot path down.
    return null;
  }
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/**
 * Draw the tenant's launcher icons. `iconUrl` may be null, unreachable, or
 * served without CORS — every one of those falls back to the monogram.
 * Returns null only when the environment has no usable 2D canvas at all
 * (a headless test DOM), which the manifest treats as "no icons".
 */
export async function buildAppIcons(
  appName: string,
  iconUrl: string | null,
  primaryColour: string,
): Promise<AppIconSet | null> {
  const letter = appName.trim().charAt(0).toUpperCase() || "M";
  const primary = HEX_COLOUR.test(primaryColour) ? primaryColour : "#649ECE";
  const image = iconUrl ? await loadImage(iconUrl) : null;

  const any192 = render(192, 0.12, image, letter, primary);
  const any512 = render(512, 0.12, image, letter, primary);
  const maskable512 = render(512, 0.22, image, letter, primary);
  if (!any192 || !any512 || !maskable512) return null;

  return { any192, any512, maskable512 };
}
